import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { calendarApi, meetingsApi, type CalendarStatus, type MeetingKind, type MeetingRecordingView, type MeetingView } from '../../api/client';
import { AddToCalendar } from '../../components/AddToCalendar';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { fullWhen, plural } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { durationText } from '../../lib/patchText';
import { useData } from '../../lib/useData';

/**
 * Meetings (1.13.0, Teampace 2707): New meeting (now, or later with a time), its link to copy and
 * send to anyone, upcoming and past, and each meeting's page with who came and for how long. The
 * meeting itself opens on its own page (/m/<code>), the same one guests use.
 */

const linkOf = (m: Pick<MeetingView, 'path'>) => `${window.location.origin}${m.path}`;

function CopyLink({ m, size }: { m: MeetingView; size?: 'small' }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size={size}
      icon={copied ? 'check' : 'copy'}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(linkOf(m));
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        } catch {
          window.prompt('Copy the meeting’s link:', linkOf(m));
        }
      }}
    >
      {copied ? 'Copied' : 'Copy link'}
    </Button>
  );
}

/** "Now" when it has no time; else its day and time. */
function whenOf(m: MeetingView): string {
  if (m.live) return 'Going on now';
  return m.startsAt ? fullWhen(m.startsAt) : 'Any time';
}

function MeetingRow({ m, base }: { m: MeetingView; base: string }) {
  const navigate = useNavigate();
  return (
    <li className="meeting-row">
      <Icon name="video" size={18} />
      <span className="cell-stack grow">
        <Link to={`${base}/meetings/${encodeURIComponent(m.id)}`}>
          <strong>{m.title}</strong>
        </Link>
        <small className="muted">
          {whenOf(m)} · {m.kind === 'webinar' ? 'Webinar' : 'Meeting'} · {m.hostName}
        </small>
      </span>
      {m.live && <Chip tone="ok">Live</Chip>}
      <CopyLink m={m} size="small" />
      <Button size="small" variant="primary" icon="video" onClick={() => navigate(m.path)}>
        Join
      </Button>
    </li>
  );
}

/** A time from a datetime-local input (the browser's own zone), as ISO. */
const isoOf = (local: string) => (local ? new Date(local).toISOString() : null);

function NewMeeting({ domain, open, onClose, onMade, calendar }: { domain: string; open: boolean; onClose: () => void; onMade: (m: MeetingView) => void; calendar: string | null }) {
  const [title, setTitle] = useState('');
  const [later, setLater] = useState(false);
  const [starts, setStarts] = useState('');
  const [minutes, setMinutes] = useState('30');
  const [kind, setKind] = useState<MeetingKind>('meeting');
  const [lobby, setLobby] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (later && !starts) return setError('Choose when it starts.');
    setBusy(true);
    setError(null);
    try {
      const startsAt = later ? isoOf(starts) : null;
      const endsAt = startsAt ? new Date(Date.parse(startsAt) + Math.max(5, Number(minutes) || 30) * 60_000).toISOString() : null;
      onMade(await meetingsApi.create(domain, { title: title.trim(), kind, lobby, startsAt, endsAt }));
      setTitle('');
      setLater(false);
      setStarts('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The meeting couldn’t be set up.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New meeting"
      subtitle="Anyone with its link can ask to join. People in your company go straight in."
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? 'Setting up…' : later ? 'Set it up' : 'Start it'}
          </Button>
        </>
      }
    >
      <form className="stack" onSubmit={(e) => void submit(e)}>
        <Field label="Name" placeholder="Weekly huddle" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
        <fieldset className="choice-row" aria-label="When">
          <label>
            <input type="radio" name="when" checked={!later} onChange={() => setLater(false)} /> Now
          </label>
          <label>
            <input type="radio" name="when" checked={later} onChange={() => setLater(true)} /> Later
          </label>
        </fieldset>
        {later && (
          <div className="form-row">
            <Field label="Starts" type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} />
            <Field label="Minutes" type="number" min={5} max={720} step={5} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
          </div>
        )}
        {later && calendar && <p className="muted small">It’s added to your Google Calendar ({calendar}) with its link.</p>}
        <label className="check">
          <input type="checkbox" checked={kind === 'webinar'} onChange={(e) => setKind(e.target.checked ? 'webinar' : 'meeting')} />
          <span>
            <strong>Webinar</strong>
            <small className="muted">Only you and the presenters you choose talk and share; everyone else watches and listens.</small>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={lobby} onChange={(e) => setLobby(e.target.checked)} />
          <span>
            <strong>Guests wait in the lobby</strong>
            <small className="muted">Someone from your company lets each guest in. Off: anyone with the link comes straight in.</small>
          </span>
        </label>
        {error && <Notice tone="crit">{error}</Notice>}
      </form>
    </Dialog>
  );
}

/**
 * Your Google Calendar: connect it once and meetings you set up for later go into it (and follow
 * it when you move them there). Shown only once Stitch staff have set Google sign-in up.
 */
function CalendarCard({ status, reload, back }: { status: CalendarStatus; reload: () => void; back: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const said = params.get('calendar');
  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      window.location.assign((await calendarApi.connectGoogle(back)).url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google Calendar couldn’t be reached.');
      setBusy(false);
    }
  };
  const g = status.google;
  return (
    <Card title="Your calendar">
      <div className="card-body stack">
        {said === 'connected' && g && (
          <Notice tone="ok" title="Google Calendar connected">
            Meetings you set up for later go into it from now on.
          </Notice>
        )}
        {said === 'error' && <Notice tone="crit">{params.get('why') || 'Google Calendar wasn’t connected. Try again.'}</Notice>}
        {error && <Notice tone="crit">{error}</Notice>}
        {g?.problem && <Notice tone="warn">{g.problem}</Notice>}
        <div className="calendar-connect">
          <p>
            {g ? (
              <>
                Google Calendar: connected as <strong>{g.email || 'your Google account'}</strong>. Meetings you set up for later are added with their link, and moving one there moves it here.
              </>
            ) : (
              'Connect your Google Calendar, and meetings you set up for later are added to it with their link. Move one there and it moves here too.'
            )}
          </p>
          {g ? (
            <>
              {g.problem && (
                <Button variant="primary" disabled={busy} onClick={() => void connect()}>
                  Connect again
                </Button>
              )}
              <Button
                variant="ghost"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await calendarApi.disconnectGoogle();
                    if (said) setParams({}, { replace: true });
                    reload();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'That didn’t work.');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Disconnect
              </Button>
            </>
          ) : (
            <Button variant="primary" disabled={busy} onClick={() => void connect()}>
              Connect Google Calendar
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

export function TenantMeetings() {
  const { tenant } = useTenant();
  const domain = tenant.domain;
  const base = `/t/${encodeURIComponent(domain)}`;
  const navigate = useNavigate();
  const list = useData(() => meetingsApi.list(domain), [domain], { live: false });
  const calendar = useData(() => calendarApi.status().catch(() => ({ available: false, google: null }) as CalendarStatus), [], { live: false });
  const [creating, setCreating] = useState(false);
  const meetings = list.data?.meetings ?? [];
  const now = Date.now();
  const upcoming = meetings.filter((m) => m.live || !m.startsAt || Date.parse(m.endsAt ?? m.startsAt) > now).sort((a, b) => Number(b.live) - Number(a.live) || Date.parse(a.startsAt ?? '9999') - Date.parse(b.startsAt ?? '9999'));
  const past = meetings.filter((m) => !upcoming.includes(m));
  return (
    <>
      <PageHeader
        title="Meetings"
        actions={
          list.data?.enabled !== false && (
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              New meeting
            </Button>
          )
        }
      />
      <div className="content stack">
        {list.error && !list.data ? (
          <LoadError error={list.error} retry={list.reload} />
        ) : !list.data ? (
          <Loading label="Loading meetings" />
        ) : !list.data.enabled ? (
          <Notice title="Meetings aren’t set up yet">Stitch support is setting up meetings for your company. Check back soon.</Notice>
        ) : (
          <>
            <Card title="Upcoming">
              {upcoming.length ? (
                <ul className="meeting-list card-body">
                  {upcoming.map((m) => (
                    <MeetingRow key={m.id} m={m} base={base} />
                  ))}
                </ul>
              ) : (
                <div className="card-body">
                  <Empty title="No meetings yet">Start one now, or set one up for later, and send its link to anyone.</Empty>
                </div>
              )}
            </Card>
            {calendar.data?.available && <CalendarCard status={calendar.data} reload={calendar.reload} back={`${base}/meetings`} />}
            {past.length > 0 && (
              <Card title="Past">
                <ul className="meeting-list card-body">
                  {past.map((m) => (
                    <MeetingRow key={m.id} m={m} base={base} />
                  ))}
                </ul>
              </Card>
            )}
          </>
        )}
      </div>
      <NewMeeting
        domain={domain}
        calendar={calendar.data?.google && !calendar.data.google.problem ? calendar.data.google.email || 'connected' : null}
        open={creating}
        onClose={() => setCreating(false)}
        onMade={(m) => {
          setCreating(false);
          // A meeting for now: straight in. One for later: its page, with the link to send.
          if (!m.startsAt) navigate(m.path);
          else navigate(`${base}/meetings/${encodeURIComponent(m.id)}`);
        }}
      />
    </>
  );
}

/** One recording: watch it, download it, read its transcript; its host (or an admin) deletes it. */
function RecordingItem({ r, domain, meetingId, onDeleted }: { r: MeetingRecordingView; domain: string; meetingId: string; onDeleted: () => void }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const when = `${fullWhen(r.startedAt)}${r.seconds ? ` · ${durationText(r.seconds)}` : ''}`;
  return (
    <li>
      <div className="row-actions" style={{ justifyContent: 'space-between' }}>
        <span className="cell-stack">
          <strong>{when}</strong>
          <small className="muted">Recorded by {r.startedByName || 'someone in your company'}</small>
        </span>
        {r.state === 'ready' && (
          <span className="row-actions">
            <a className="btn secondary small" href={meetingsApi.recordingUrl(domain, meetingId, r.id, 'video', true)} download>
              Download
            </a>
            {r.canDelete && (
              <Button size="small" variant="ghost" onClick={() => setAsking(true)}>
                Delete
              </Button>
            )}
          </span>
        )}
      </div>
      {r.state === 'ready' ? (
        <video controls preload="metadata" src={meetingsApi.recordingUrl(domain, meetingId, r.id, 'video')} data-private="" />
      ) : r.state === 'failed' ? (
        <Notice tone="crit">This recording didn’t work out: there’s nothing to watch.</Notice>
      ) : (
        <p className="muted">{r.state === 'recording' ? 'Recording now…' : 'Finishing the recording. It’s ready in a minute or two.'}</p>
      )}
      {r.transcript?.status === 'done' && (
        <details>
          <summary>Transcript</summary>
          <pre className="meeting-transcript">{r.transcript.text || 'Nobody could be heard.'}</pre>
        </details>
      )}
      {r.transcript?.status === 'pending' && <p className="muted small">The transcript is being written out.</p>}
      {r.transcript?.status === 'failed' && <p className="muted small">The transcript couldn’t be written out{r.transcript.error ? `: ${r.transcript.error}` : '.'}</p>}
      {error && <Notice tone="crit">{error}</Notice>}
      <Dialog
        open={asking}
        onClose={() => setAsking(false)}
        title="Delete this recording?"
        subtitle="The video, its sound and its transcript are deleted for good."
        foot={
          <>
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await meetingsApi.deleteRecording(domain, meetingId, r.id);
                  setAsking(false);
                  onDeleted();
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'It couldn’t be deleted.');
                  setAsking(false);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Delete recording
            </Button>
          </>
        }
      >
        <p>{when}</p>
      </Dialog>
    </li>
  );
}

/** One meeting: its link, when, its settings (for its host or an admin), and who came to each run. */
export function MeetingDetail() {
  const { tenant } = useTenant();
  const domain = tenant.domain;
  const { id = '' } = useParams();
  const base = `/t/${encodeURIComponent(domain)}`;
  const navigate = useNavigate();
  const data = useData(() => meetingsApi.get(domain, id), [domain, id], { live: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const m = data.data?.meeting;
  const change = async (body: Parameters<typeof meetingsApi.update>[2]) => {
    setBusy(true);
    setError(null);
    try {
      await meetingsApi.update(domain, id, body);
      data.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusy(false);
    }
  };
  if (data.error && !data.data) return <LoadError error={data.error} retry={data.reload} />;
  if (!m) return <Loading label="Loading the meeting" />;
  return (
    <>
      <PageHeader
        eyebrow={<Link to={`${base}/meetings`}>Meetings</Link>}
        title={m.title}
        chips={m.live ? <Chip tone="ok">Live</Chip> : m.cancelled ? <Chip tone="crit">Cancelled</Chip> : undefined}
        actions={
          !m.cancelled && (
            <>
              {m.startsAt && m.endsAt && <AddToCalendar m={{ title: m.title, startsAt: m.startsAt, endsAt: m.endsAt, link: linkOf(m), company: tenant.name }} ics={meetingsApi.icsUrl(domain, m.id)} />}
              <CopyLink m={m} />
              <Button variant="primary" icon="video" onClick={() => navigate(m.path)}>
                Join
              </Button>
            </>
          )
        }
      />
      <div className="content stack">
        {error && <Notice tone="crit">{error}</Notice>}
        <Card title="About it">
          <div className="card-body stack">
          <dl className="facts">
            <dt>When</dt>
            <dd>{whenOf(m)}</dd>
            <dt>Kind</dt>
            <dd>{m.kind === 'webinar' ? 'Webinar: the host and presenters talk and share; everyone else watches.' : 'Meeting: everyone can talk and share.'}</dd>
            <dt>Guests</dt>
            <dd>{m.lobby ? 'Wait in the lobby until someone from your company lets them in.' : 'Come straight in with the link.'}</dd>
            <dt>Set up by</dt>
            <dd>{m.hostName}</dd>
            <dt>Link</dt>
            <dd className="mono">{linkOf(m)}</dd>
            {m.calendar && (
              <>
                <dt>Calendar</dt>
                <dd>{m.calendar.mine ? 'In your Google Calendar: move it there and it moves here too.' : 'In its host’s Google Calendar.'}</dd>
              </>
            )}
          </dl>
          {m.canChange && !m.cancelled && (
            <div className="row-actions">
              <Button disabled={busy} onClick={() => void change({ lobby: !m.lobby })}>
                {m.lobby ? 'Let guests straight in' : 'Make guests wait in the lobby'}
              </Button>
              <Button disabled={busy || m.live} onClick={() => void change({ kind: m.kind === 'webinar' ? 'meeting' : 'webinar' })}>
                {m.kind === 'webinar' ? 'Make it a meeting' : 'Make it a webinar'}
              </Button>
              <Button variant="danger" disabled={busy} onClick={() => setCancelling(true)}>
                Cancel meeting
              </Button>
            </div>
          )}
          </div>
        </Card>
        {data.data!.recordings.length > 0 && (
          <Card title="Recordings">
            <div className="card-body">
              <ul className="meeting-recordings">
                {data.data!.recordings.map((r) => (
                  <RecordingItem key={r.id} r={r} domain={domain} meetingId={m.id} onDeleted={data.reload} />
                ))}
              </ul>
            </div>
          </Card>
        )}
        <Card title="Who came">
          <div className="card-body">
          {data.data!.runs.length ? (
            <ul className="meeting-runs">
              {data.data!.runs.map((r) => (
                <li key={r.startedAt}>
                  <strong>
                    {fullWhen(r.startedAt)} · {r.endedAt ? durationText(r.seconds) : 'going on now'}
                  </strong>
                  <span className="muted">{plural(r.people.length, 'person', 'people')}</span>
                  <ul>
                    {r.people.map((p, i) => (
                      <li key={`${p.name}-${i}`}>
                        {p.name}
                        {p.guest && <Chip small>Guest</Chip>}
                        <span className="muted meeting-run-time">{durationText(p.seconds)}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          ) : (
            <Empty title="Nobody yet">Who comes, and for how long, shows here after each meeting.</Empty>
          )}
          </div>
        </Card>
      </div>
      <Dialog
        open={cancelling}
        onClose={() => setCancelling(false)}
        title="Cancel this meeting?"
        subtitle="Its link stops working, and anyone in it now is let go."
        foot={
          <>
            <Button variant="ghost" onClick={() => setCancelling(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await meetingsApi.cancel(domain, id);
                  navigate(`${base}/meetings`);
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'It couldn’t be cancelled.');
                  setCancelling(false);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Cancel meeting
            </Button>
          </>
        }
      >
        <p>People you sent the link to will see that it isn’t working.</p>
      </Dialog>
    </>
  );
}
